import React, { useEffect, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';

import useEntries from '../components/Common/useEntries';
import DirectorateProfile from '../components/Directorate/DirectorateProfile';
import SinglePublication from '../components/Publications/SinglePublication';
import PublicationList from '../components/Publications/PublicationList';
import EventList from '../components/Events/EventList';
import SingleEvent from '../components/Events/SingleEvent';
import DynamicPage from '../components/Pages/DynamicPage';
import { LoadingState, Section, EmptyState } from '../components/Common/PageLayout';
import SubdomainHighlights from './SubdomainHighlights';
import { initializeStrapiBackend } from '../components/Contentful_API/strapiBackend';
import { fetchSite, findDirectorateForSubdomain, getMainPortalUrl, getSubdomainTitle } from './portal';

/** Sends any page a subdomain doesn't serve to the same path on mof.gov.tl. */
export const ToMainPortal = ({ subdomain }) => {
    const { pathname, search, hash } = useLocation();
    useEffect(() => {
        // Drop the local-testing ?subdomain= flag, or the redirect would loop.
        const params = new URLSearchParams(search);
        params.delete('subdomain');
        const query = params.toString() ? `?${params}` : '';
        window.location.replace(getMainPortalUrl(subdomain, `${pathname}${query}${hash}`));
    }, [subdomain, pathname, search, hash]);
    return <LoadingState label="Opening the Ministry of Finance portal" />;
};

/**
 * The directorate a subdomain belongs to: from its Site entry in the CMS, or, for subdomains
 * that have no Site yet, by matching directorate names against the built-in keyword list.
 */
function useSubdomainDirectorate(subdomain) {
    const [site, setSite] = useState({ loading: true, entry: null });
    const fallback = useEntries({ content_type: 'directorates', limit: 200 });

    useEffect(() => {
        let active = true;
        initializeStrapiBackend()
            .then(({ apiUrl }) => fetchSite(apiUrl, subdomain))
            .then((entry) => active && setSite({ loading: false, entry }))
            .catch(() => active && setSite({ loading: false, entry: null }));
        return () => { active = false; };
    }, [subdomain]);

    if (site.loading) return { loading: true };
    if (site.entry?.directorateSlug) {
        return { loading: false, slug: site.entry.directorateSlug, title: site.entry.title };
    }
    if (fallback.loading) return { loading: true };
    const matched = findDirectorateForSubdomain(fallback.items, subdomain);
    return { loading: false, slug: matched?.fields?.slug || null, title: site.entry?.title || getSubdomainTitle(subdomain) };
}

const Unavailable = ({ title }) => (
    <Section>
        <EmptyState
            title={title}
            description="This portal is not available right now. Use “Back to main portal” above to visit the Ministry of Finance website."
        />
    </Section>
);

/**
 * Routes on a directorate subdomain such as budget.mof.gov.tl. The home page, the
 * directorate's publications and news, and pages reached from the subdomain's own menus are
 * served here; every other path belongs to the main portal and is redirected there.
 */
const SubdomainRoutes = ({ subdomain }) => {
    const directorate = useSubdomainDirectorate(subdomain);

    if (directorate.loading) return <LoadingState label={`Loading ${getSubdomainTitle(subdomain)}`} />;
    if (!directorate.slug) return <Unavailable title={directorate.title} />;

    const slug = directorate.slug;
    return (
        <Switch>
            <Route
                exact
                path="/"
                render={() => (
                    <>
                        <DirectorateProfile slug={slug} onSubdomain />
                        <SubdomainHighlights directorateSlug={slug} />
                    </>
                )}
            />
            <Route path="/publicationdetails/:slug" component={SinglePublication} />
            <Route path="/publications" render={() => <PublicationList directorateSlug={slug} />} />
            <Route path="/eventdetails/:slug" component={SingleEvent} />
            <Route path="/latestnews" render={() => <EventList directorateSlug={slug} />} />
            <Route path="/pagedetails/:slug" component={DynamicPage} />
            <Route render={() => <ToMainPortal subdomain={subdomain} />} />
        </Switch>
    );
};

export default SubdomainRoutes;
